<template>
  <div class="bg-white rounded-lg overflow-hidden flex flex-col h-[85vh]">
    <!-- Header -->
    <!-- <div class="flex justify-between items-center p-3">
      <div class="flex items-center gap-2">
        <v-icon color="primary">mdi-map-marker-radius</v-icon>
        <h3 class="text-xl font-semibold text-gray-800">Live Field Force Tracking</h3>
      </div>
      <div>
        <v-btn icon @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>
    </div> -->

    <!-- Body -->
    <div class="flex flex-1 overflow-hidden" v-if="userLiveLocation && userLiveLocation.length > 0">
      <!-- Sidebar List (Pending Activation) -->
      <div
        class="w-80 border-r border-gray-200 flex flex-col bg-gray-50 flex-shrink-0 h-full z-10 relative"
      >
        <div class="p-4 bg-white border-b flex-shrink-0">
          <h4 class="font-semibold text-gray-700">
            {{ focusedUserId ? 'Route History' : 'Active Users' }}
            <span v-if="focusedUserId" class="text-sm font-bold text-gray-500 ml-1">
              (Total: {{ totalDistance }} km)
            </span>
            <span v-else>
              ({{ visibleUsers.length }}{{ totalUsers ? ` / ${totalUsers}` : '' }})
            </span>
          </h4>
          <div class="mt-2 relative" v-if="!focusedUserId">
            <v-text-field
              dense
              outlined
              hide-details
              placeholder="Search user..."
              prepend-inner-icon="mdi-magnify"
              class="rounded-lg text-sm"
            ></v-text-field>
          </div>
        </div>
        <div class="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-2">
          <div
            v-for="(user, index) in visibleUsers"
            :key="user._id || index"
            class="bg-white p-3 rounded-lg shadow-sm border border-gray-100 cursor-pointer hover:border-primary transition-colors"
            :class="{
              'border-primary bg-blue-50':
                (focusedUserId ? user._id : user.userId._id || user.userId) === selectedUserId
            }"
            @click="selectUser(user)"
          >
            <div class="flex items-center gap-3">
              <v-avatar size="36" color="primary lighten-4" v-if="!focusedUserId">
                <span class="primary--text font-weight-bold text-sm">
                  {{ getFullName(user).charAt(0) }}
                </span>
              </v-avatar>
              <div class="flex-1 min-w-0">
                <div v-if="focusedUserId" class="flex flex-col gap-1 mt-1">
                  <div class="flex items-center justify-between text-xs text-gray-700">
                    <span class="font-bold">Arrived</span>
                    <span>{{ formatTime(user.startTime) }}</span>
                  </div>
                  <div class="flex items-center justify-between text-xs text-gray-700">
                    <span class="font-bold">Departed</span>
                    <span>{{ formatTime(user.endTime) }}</span>
                  </div>
                  <div class="flex items-center justify-between text-xs text-primary font-bold">
                    <span>Duration</span>
                    <span>{{ user.durationText }}</span>
                  </div>
                  <div
                    class="flex items-center justify-between text-xs text-gray-700"
                    v-if="user.deviceName"
                  >
                    <span class="font-bold">Device</span>
                    <span class="truncate max-w-[120px]" :title="user.deviceName">{{
                      user.deviceName
                    }}</span>
                  </div>
                  <!-- Activity/Action Summary in Sidebar -->
                  <div
                    v-if="showHistoryDetails && user.actions && user.actions.length > 0"
                    class="mt-2 border-t border-dashed border-gray-200 pt-2"
                  >
                    <p class="text-xs font-semibold text-gray-600 mb-1">
                      Actions ({{ user.actions.length }})
                    </p>
                    <div class="flex flex-wrap gap-1">
                      <v-chip
                        v-for="(count, action) in getCounts(user.actions)"
                        :key="action"
                        x-small
                        label
                        color="blue lighten-5"
                        text-color="blue darken-2"
                        class="px-1"
                      >
                        {{ action }}: {{ count }}
                      </v-chip>
                    </div>
                  </div>
                  <div
                    v-if="showHistoryDetails && user.activities && user.activities.length > 0"
                    class="mt-2 border-t border-dashed border-gray-200 pt-2"
                  >
                    <p class="text-xs font-semibold text-gray-600 mb-1">
                      Activities ({{ user.activities.length }})
                    </p>
                    <div class="flex flex-wrap gap-1">
                      <v-chip
                        v-for="(count, activity) in getCounts(user.activities)"
                        :key="activity"
                        x-small
                        label
                        color="orange lighten-5"
                        text-color="orange darken-4"
                        class="px-1"
                      >
                        {{ activity }}: {{ count }}
                      </v-chip>
                    </div>
                  </div>
                </div>
                <h5 v-else class="font-medium text-sm text-gray-900 truncate">
                  {{ getFullName(user) }}
                </h5>
                <p v-if="!focusedUserId" class="text-sm text-gray-500 truncate">
                  {{ getLastUpdateTime(user) }}
                </p>
              </div>
              <v-icon small color="gray lighten-1">mdi-chevron-right</v-icon>
            </div>
            <!-- <v-divider class="my-2"></v-divider> -->
            <div class="mt-2 flex items-start text-xs text-gray-500">
              <v-icon x-small class="mr-1 mt-0.5" color="gray">mdi-map-marker</v-icon>
              <span class="line-clamp-2 font-semibold text-gray-500">{{
                getLastLocation(user).address || 'No address'
              }}</span>
            </div>
            <div
              class="mt-1 flex items-center text-xs text-gray-500"
              v-if="user.batteryLevel !== undefined && user.batteryLevel !== null"
            >
              <v-icon x-small class="mr-1" :color="getBatteryColor(user.batteryLevel)">
                {{ getBatteryIcon(user.batteryLevel) }}
              </v-icon>
              <span :class="getBatteryColor(user.batteryLevel) + '--text'">
                {{ user.batteryLevel.toFixed(0) }}%
              </span>
            </div>
            <v-divider class="my-1"></v-divider>
            <div class="flex mt-2 align-center">
              <span class="font-semibold text-xs">Source:</span>
              <span>
                <img
                  :src="getSourceIcon(user)"
                  :alt="user.deviceName || 'Device'"
                  class="w-5 ml-2"
                  :title="`${user.source || 'Unknown'} - ${user.deviceName || ''}`"
                />
              </span>
            </div>
          </div>
          <div v-if="!focusedUserId && pageCount > 1" class="p-2 border-t mt-auto text-center">
            <v-pagination
              :value="currentPage"
              :length="pageCount"
              :total-visible="5"
              circle
              small
              @input="$emit('page-change', $event)"
              :disabled="isLoading"
            ></v-pagination>
          </div>
        </div>
      </div>

      <!-- Map Area -->
      <div class="w-full relative h-full">
        <div ref="map" class="w-full h-full"></div>
        <!-- Floating Refresh Button -->
        <div class="flex items-start align-center absolute top-4 left-0 w-full px-4 z-10 gap-3">
          <v-alert
            v-if="trackingConfig"
            v-model="alert"
            border="left"
            close-text="Close Alert"
            color="blue"
            dark
            dismissible
            class="flex-grow-1 mb-0 py-2 text-sm"
            dense
          >
            <span class="w-full">
              Location data is captured while the user is checked in. Updates occur every
              <strong>{{ calculateInterval }}</strong
              >, or when the user moves <strong>{{ trackingConfig.min_distance }} meters</strong>,
              with <strong>{{ trackingConfig.accuracy }}</strong> accuracy.
            </span>
          </v-alert>
          <v-btn
            color="white"
            small
            @click="refreshMap"
            class="box-shadow-md rounded-lg normal-case ml-auto"
            title="Refresh Locations"
          >
            <v-icon left small color="primary">mdi-refresh</v-icon>
            <span class="text-primary font-semibold">Refresh</span>
          </v-btn>
        </div>

        <!-- Bottom Sheet Details Panel -->
        <div
          v-if="selectedGroup"
          class="absolute bottom-0 left-0 w-full bg-white z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] rounded-t-xl overflow-hidden flex flex-col transition-transform duration-300 ease-in-out"
          style="height: 500px; max-height: 50vh"
        >
          <!-- Content -->
          <div class="flex-1 overflow-y-auto p-4 custom-scrollbar">
            <div class="flex justify-between items-center mb-4">
              <div class="flex-1 min-w-0 mr-2">
                <h4 class="font-semibold text-gray-800 text-sm truncate m-0 leading-tight">
                  {{
                    focusedUserId
                      ? `Stop ${selectedGroup.index} Details`
                      : getFullName(selectedGroup)
                  }}
                </h4>
                <p
                  class="text-[12px] text-gray-500 flex items-center gap-1 truncate m-0 mt-0.5 leading-none"
                >
                  <v-icon x-small color="gray" style="font-size: 11px">mdi-map-marker</v-icon>
                  <span class="truncate">
                    {{
                      selectedGroup.address ||
                      selectedGroup.normalizedLocation?.address ||
                      'Address not available'
                    }}
                  </span>
                </p>
              </div>
              <div>
                <v-btn icon small @click="selectedGroup = null" class="flex-shrink-0">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>
            <!-- Stats Row -->
            <div class="flex flex-wrap gap-4 mb-4 text-sm text-gray-700">
              <div class="bg-blue-50 px-3 py-2 rounded-lg" v-if="selectedGroup.startTime">
                <span class="block text-xs font-bold text-blue-700">Arrival</span>
                {{ formatTime(selectedGroup.startTime) }}
              </div>
              <div class="bg-orange-50 px-3 py-2 rounded-lg" v-if="selectedGroup.endTime">
                <span class="block text-xs font-bold text-orange-700">Departure</span>
                {{ formatTime(selectedGroup.endTime) }}
              </div>
              <div class="bg-gray-100 px-3 py-2 rounded-lg" v-if="selectedGroup.durationText">
                <span class="block text-xs font-bold text-gray-600">Duration</span>
                {{ selectedGroup.durationText }}
              </div>
              <div class="bg-green-50 px-3 py-2 rounded-lg" v-if="selectedGroup.batteryLevel">
                <span class="block text-xs font-bold text-green-700">Battery</span>
                {{ selectedGroup.batteryLevel.toFixed(0) }}%
              </div>
              <div class="bg-purple-50 px-3 py-2 rounded-lg" v-if="selectedGroup.entryCount">
                <span class="block text-xs font-bold text-purple-700">Entries</span>
                {{ selectedGroup.entryCount }}
              </div>
            </div>

            <!-- Detailed Points List -->
            <div v-if="groupedDisplayPoints && groupedDisplayPoints.length > 0">
              <h5 class="font-semibold text-gray-800 mb-2">Captured Points</h5>
              <div class="space-y-3">
                <div
                  v-for="(item, index) in groupedDisplayPoints"
                  :key="index"
                  class="border rounded-lg p-3 hover:bg-gray-50 transition-colors border-blue-200 bg-blue-50"
                >
                  <div class="flex justify-between items-start mb-2">
                    <div class="flex items-center gap-2">
                      <span class="font-medium text-xs text-gray-500">#{{ index + 1 }}</span>
                      <span class="text-xs font-semibold text-gray-600">
                        {{ $dayjs(item.timestamp).format('hh:mm:ss A') }}
                      </span>
                    </div>
                  </div>

                  <!-- Task Group Display -->
                  <div
                    v-if="item.isTaskGroup"
                    class="mt-2 text-sm bg-white p-2 rounded border border-gray-100"
                  >
                    <!-- Task Details Card -->
                    <div
                      v-if="taskDetailsMap[item.taskId]"
                      class="bg-gray-50 p-2 rounded text-xs border border-gray-200"
                    >
                      <div class="font-semibold text-gray-700 mb-1 border-b border-gray-200 pb-1">
                        Task Details
                      </div>
                      <div class="grid grid-cols-2 gap-x-2 gap-y-1">
                        <div v-if="taskDetailsMap[item.taskId].case?.caseNo">
                          <span class="text-gray-500">Case No:</span>
                          <span class="text-gray-800 ml-1 font-medium">{{
                            taskDetailsMap[item.taskId].case.caseNo
                          }}</span>
                        </div>
                        <div v-if="taskDetailsMap[item.taskId].case?.customer?.customerFullName">
                          <span class="text-gray-500">Customer:</span>
                          <span class="text-gray-800 ml-1">{{
                            taskDetailsMap[item.taskId].case.customer.customerFullName
                          }}</span>
                        </div>
                        <div v-if="taskDetailsMap[item.taskId].taskStatus">
                          <span class="text-gray-500">Task Status:</span>
                          <span class="text-gray-800 ml-1">{{
                            taskDetailsMap[item.taskId].taskStatus
                          }}</span>
                        </div>
                      </div>

                      <!-- Meeting Details -->
                      <div
                        v-if="taskDetailsMap[item.taskId].meetingDetails"
                        class="mt-2 pt-2 border-t border-gray-100"
                      >
                        <div class="text-xs font-semibold text-gray-600 mb-1">Meeting Details</div>
                        <div class="grid grid-cols-2 gap-x-2 gap-y-1">
                          <div v-if="taskDetailsMap[item.taskId].meetingDetails.meetingStatus">
                            <span class="text-gray-500">Status:</span>
                            <span class="text-gray-800 ml-1">{{
                              taskDetailsMap[item.taskId].meetingDetails.meetingStatus
                            }}</span>
                          </div>
                          <div
                            v-if="taskDetailsMap[item.taskId].meetingDetails.meetingNotes"
                            class="col-span-2"
                          >
                            <span class="text-gray-500 block">Notes:</span>
                            <p class="text-gray-800 italic mt-0.5">
                              {{ taskDetailsMap[item.taskId].meetingDetails.meetingNotes }}
                            </p>
                          </div>
                        </div>
                      </div>

                      <!-- Selfie -->
                      <div
                        v-if="taskDetailsMap[item.taskId].additionalInfo?.selfie"
                        class="mt-2 pt-2 border-t border-gray-100"
                      >
                        <div class="text-xs font-semibold text-gray-600 mb-1">Selfie</div>
                        <div class="flex items-center">
                          <a
                            :href="`${s3BaseUrl}${
                              taskDetailsMap[item.taskId].additionalInfo.selfie.doc_physical_path
                            }`"
                            target="_blank"
                            class="relative block border rounded-lg overflow-hidden w-16 h-16 hover:opacity-90"
                          >
                            <img
                              :src="`${s3BaseUrl}${
                                taskDetailsMap[item.taskId].additionalInfo.selfie.doc_physical_path
                              }`"
                              class="w-full h-full object-cover"
                              alt="Selfie"
                            />
                            <div
                              class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-10 transition-all"
                            >
                              <v-icon x-small color="white" class="drop-shadow-md"
                                >mdi-open-in-new</v-icon
                              >
                            </div>
                          </a>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-xs text-gray-400 mt-1 pl-1">
                      <span class="text-gray-500 mr-1">ID:</span>
                      <span class="font-mono">{{ item.taskId }}</span>
                    </div>

                    <!-- Activities List -->
                    <div class="mt-2 pt-2 border-t border-gray-200">
                      <div class="text-xs font-semibold text-gray-600 mb-2">
                        Activity Log ({{ item.activities.length }})
                      </div>
                      <div
                        v-for="(act, aIndex) in item.activities"
                        :key="aIndex"
                        class="flex justify-between items-center text-xs mb-1.5 p-1 hover:bg-gray-50 rounded"
                      >
                        <div class="flex items-center gap-2">
                          <v-icon x-small color="primary" class="mt-0.5">mdi-circle-small</v-icon>
                          <div>
                            <span class="font-medium text-gray-700 block">{{
                              act.action || act.type
                            }}</span>
                          </div>
                        </div>
                        <span class="font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{{
                          $dayjs(act.time).format('hh:mm:ss A')
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Standalone Point Display -->
                  <div v-else class="mt-2 text-sm bg-white p-2 rounded border border-gray-100">
                    <div class="grid grid-cols-2 gap-2">
                      <div>
                        <span class="text-xs text-gray-500 block">Type</span>
                        <span class="font-medium text-gray-800">{{
                          item.point.activity.activityType || '-'
                        }}</span>
                      </div>
                      <div>
                        <span class="text-xs text-gray-500 block">Action</span>
                        <span class="font-medium text-gray-800">{{
                          item.point.activity.action || '-'
                        }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center text-gray-400 py-8">
              No detailed points data available.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex flex-col items-center justify-center flex-1 h-full">
      <v-icon size="64" color="gray lighten-2">mdi-map-marker-off</v-icon>
      <h3 class="text-lg font-medium text-gray-500 mt-4">No Data Available For Selected Date</h3>
      <p class="text-gray-400">Try adjusting the date filter</p>
    </div>
  </div>
</template>

<script>
/* global google */
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import androidIcon from '@/assets/img/android_icon.png'
import chromeIcon from '@/assets/img/chrome_icon.png'
import safariIcon from '@/assets/img/safari_icon.png'
import firefoxIcon from '@/assets/img/firefox_icon.png'
import edgeIcon from '@/assets/img/edge_icon.png'
import explorerIcon from '@/assets/img/explorer_icon.png'
dayjs.extend(relativeTime)

export default {
  name: 'MapView',
  props: {
    userLiveLocation: {
      type: Array,
      default: () => []
    },
    focusedUserId: {
      type: String,
      default: null
    },
    totalUsers: {
      type: Number,
      default: 0
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    currentPage: {
      type: Number,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    showHistoryDetails: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      sourceIcons: {
        android: androidIcon,
        chrome: chromeIcon,
        safari: safariIcon,
        firefox: firefoxIcon,
        edge: edgeIcon,
        explorer: explorerIcon
      },
      map: null,
      zoom: 12,
      center: { lat: 20.5937, lng: 78.9629 }, // Default Center
      markers: [],
      markersMap: new Map(),
      initialFitDone: false,
      selectedUserId: null,
      infoWindowInstance: null,
      AdvancedMarkerElement: null,
      PinElement: null,
      isInitializing: false,
      directionsService: null,
      directionsRenderer: null,
      initMapRetries: 0,
      alert: true,
      fallbackPolyline: null,
      trackingConfig: null,
      selectedGroup: null,
      taskDetailsMap: {}
    }
  },
  computed: {
    // Process raw location data into unique latest user locations
    processedUsers() {
      if (!this.userLiveLocation || !this.userLiveLocation.length) return []

      // IF detailed view for a single user (History Mode)
      if (this.focusedUserId) {
        return this.userLiveLocation
          .map((record) => ({
            ...record,
            timestamp: new Date(record.updatedAt || record.createdAt).getTime(),
            normalizedLocation: this.extractLocation(record)
          }))
          .sort((a, b) => a.timestamp - b.timestamp)
      }

      // ELSE Summary view (Live Mode) - Latest location per user
      const userMap = new Map()

      this.userLiveLocation.forEach((record) => {
        if (!record.userId) return
        // Handle case where userId might be just a string or missing _id
        const userId =
          record.userId._id || (typeof record.userId === 'string' ? record.userId : null)
        if (!userId) return

        const existing = userMap.get(userId)

        // Compare dates to keep the latest record
        // Assuming updatedAt or createdAt is the timestamp
        const recordTime = new Date(record.updatedAt || record.createdAt).getTime()

        if (!existing || recordTime > existing.timestamp) {
          userMap.set(userId, {
            ...record,
            timestamp: recordTime,
            // Normalize location data
            normalizedLocation: this.extractLocation(record)
          })
        }
      })

      return Array.from(userMap.values())
    },
    pageCount() {
      if (!this.totalUsers || !this.itemsPerPage) return 0
      return Math.ceil(this.totalUsers / this.itemsPerPage)
    },
    visibleUsers() {
      if (this.focusedUserId) {
        return this.groupedHistory
      }
      return this.processedUsers
    },
    s3BaseUrl() {
      return process.env.VUE_APP_S3_BASE_URL
    },
    groupedHistory() {
      if (!this.focusedUserId || !this.processedUsers.length) return []

      const groups = []
      let currentGroup = null
      let groupIndex = 1

      this.processedUsers.forEach((p) => {
        if (p.state === 'STILL') {
          const pAddress = p.normalizedLocation.address || ''
          const pPrefix = pAddress.split(',')[0].trim()

          if (currentGroup) {
            // Check if address prefix matches
            if (currentGroup.addressPrefix === pPrefix) {
              // Extend current group
              currentGroup.endTime = p.timestamp
              currentGroup.points.push(p)
              currentGroup.batteryLevel = p.batteryLevel
              currentGroup.entryCount = (currentGroup.entryCount || 0) + 1
              if (p.activity && p.activity.action) currentGroup.actions.push(p.activity.action)
              if (p.activity && p.activity.activityType)
                currentGroup.activities.push(p.activity.activityType)
            } else {
              // Address changed (even if still STILL) -> Closure and New Group
              currentGroup.duration = currentGroup.endTime - currentGroup.startTime
              currentGroup.durationText = this.formatDuration(currentGroup.duration)
              groups.push(currentGroup)

              groupIndex++
              currentGroup = {
                _id: 'stop_' + groupIndex,
                userId: p.userId,
                type: 'STOP',
                index: groupIndex,
                startTime: p.timestamp,
                endTime: p.timestamp,
                address: pAddress,
                addressPrefix: pPrefix,
                lat: p.normalizedLocation.lat,
                lng: p.normalizedLocation.lng,
                batteryLevel: p.batteryLevel,
                points: [p],
                entryCount: 1,
                actions: p.activity && p.activity.action ? [p.activity.action] : [],
                activities: p.activity && p.activity.activityType ? [p.activity.activityType] : [],
                source: p.source,
                deviceName: p.deviceName
              }
            }
          } else {
            // New Group
            currentGroup = {
              _id: 'stop_' + groupIndex,
              userId: p.userId,
              type: 'STOP',
              index: groupIndex,
              startTime: p.timestamp,
              endTime: p.timestamp,
              address: pAddress,
              addressPrefix: pPrefix,
              lat: p.normalizedLocation.lat,
              lng: p.normalizedLocation.lng,
              batteryLevel: p.batteryLevel,
              points: [p],
              entryCount: 1,
              actions: p.activity && p.activity.action ? [p.activity.action] : [],
              activities: p.activity && p.activity.activityType ? [p.activity.activityType] : [],
              source: p.source,
              deviceName: p.deviceName
            }
          }
        } else {
          // MOVING
          if (currentGroup) {
            currentGroup.duration = currentGroup.endTime - currentGroup.startTime
            currentGroup.durationText = this.formatDuration(currentGroup.duration)
            groups.push(currentGroup)
            currentGroup = null
            groupIndex++
          }
        }
      })

      if (currentGroup) {
        currentGroup.duration = currentGroup.endTime - currentGroup.startTime
        currentGroup.durationText = this.formatDuration(currentGroup.duration)
        groups.push(currentGroup)
      }

      return groups
    },
    calculateInterval() {
      if (!this.trackingConfig) return 0
      if (this.trackingConfig.interval < 60) return '60 Second'
      return this.trackingConfig.interval / 60 + ' Minutes'
    },
    filteredGroupPoints() {
      if (!this.selectedGroup || !this.selectedGroup.points) return []
      return this.selectedGroup.points.filter(
        (p) =>
          p.activity &&
          (p.activity.activityType ||
            p.activity.action ||
            p.activity.taskDetails ||
            p.activity.caseTaskDetails)
      )
    },
    groupedDisplayPoints() {
      if (!this.selectedGroup || !this.selectedGroup.points) return []

      const rawPoints = this.selectedGroup.points.filter(
        (p) =>
          p.activity &&
          (p.activity.activityType ||
            p.activity.action ||
            p.activity.taskDetails ||
            p.activity.caseTaskDetails)
      )

      const result = []
      const taskGroups = {} // map taskId -> index in result

      rawPoints.forEach((point) => {
        const activity = point.activity
        const taskId = activity.caseTaskDetails || activity.taskDetails

        if (taskId) {
          // It belongs to a task
          if (taskGroups[taskId] !== undefined) {
            // Group exists, append activity
            const groupIndex = taskGroups[taskId]
            result[groupIndex].activities.push({
              action: activity.action,
              type: activity.activityType,
              time: point.timestamp || point.updatedAt || point.createdAt
            })
          } else {
            // Create new group
            const newGroup = {
              isTaskGroup: true,
              taskId: taskId,
              primaryPoint: point, // Keep ref for direct object access if needed
              timestamp: point.timestamp || point.updatedAt || point.createdAt,
              activities: [
                {
                  action: activity.action,
                  type: activity.activityType,
                  time: point.timestamp || point.updatedAt || point.createdAt
                }
              ]
            }
            result.push(newGroup)
            taskGroups[taskId] = result.length - 1
          }
        } else {
          // No task ID, standalone point
          result.push({
            isTaskGroup: false,
            point: point,
            timestamp: point.timestamp || point.updatedAt || point.createdAt
          })
        }
      })

      return result
    },
    totalDistance() {
      if (!this.focusedUserId || !this.processedUsers || this.processedUsers.length < 2) return 0

      let total = 0
      for (let i = 0; i < this.processedUsers.length - 1; i++) {
        const p1 = this.processedUsers[i].normalizedLocation
        const p2 = this.processedUsers[i + 1].normalizedLocation

        if (p1 && p1.lat && p1.lng && p2 && p2.lat && p2.lng) {
          total += this.calculateDistance(p1.lat, p1.lng, p2.lat, p2.lng)
        }
      }
      return total.toFixed(2)
    }
  },
  mounted() {
    this.loadGoogleMapsScript()
    this.fetchTrackingConfig()
  },
  watch: {
    userLiveLocation: {
      handler(val) {
        this.$nextTick(() => {
          if (!val || val.length === 0) {
            // Data cleared: Reset map instance if container is destroyed via v-if
            if (this.map) {
              // We might want to remove listeners or cleanup?
              this.map = null
              this.markersMap = new Map()
              this.initialFitDone = false
              this.directionsRenderer = null
              this.fallbackPolyline = null
            }
          } else {
            if (this.map) {
              this.updateMarkers()
            } else {
              // try to init
              this.initMap()
            }
          }
        })
      },
      deep: true
    }
  },
  methods: {
    getSourceIcon(user) {
      if (!user) return this.sourceIcons.android
      const source = (user.source || '').toUpperCase()
      const deviceName = user.deviceName || '' // Case sensitive check might be needed if original UA is preserved, but usually safer to check whatever is passed. user's snippet uses userAgent string check.

      if (source === 'WEB') {
        const userAgent = deviceName // Assuming deviceName holds the UA string or similar info for WEB
        if (userAgent.indexOf('Firefox') > -1) {
          return this.sourceIcons.firefox
        } else if (userAgent.indexOf('Trident') > -1) {
          return this.sourceIcons.explorer
        } else if (userAgent.indexOf('Edge') > -1) {
          return this.sourceIcons.edge
        } else if (userAgent.indexOf('Chrome') > -1) {
          return this.sourceIcons.chrome
        } else if (userAgent.indexOf('Safari') > -1) {
          return this.sourceIcons.safari
        }
        return this.sourceIcons.chrome
      }
      return this.sourceIcons.android
    },
    formatTime(time) {
      if (!time) return ''
      return this.$dayjs(time).format('hh:mm A')
    },
    formatInterval(seconds) {
      if (!seconds) return '0s'
      if (seconds < 60) return `${seconds}s`
      return `${Math.round(seconds / 60)}m`
    },
    async fetchTrackingConfig() {
      try {
        const response = await this.$axios.get('/orgSetup/live-tracking-config')
        if (response.data && response.data.success) {
          this.trackingConfig = response.data.data
        }
      } catch (error) {
        console.error('Error fetching tracking config:', error)
      }
    },
    refreshMap() {
      this.$emit('refresh')
    },
    extractLocation(record) {
      // Handle GeoJSON format
      if (record.location && record.location.coordinates) {
        return {
          lat: record.location.coordinates[1],
          lng: record.location.coordinates[0],
          address: record.location?.address || 'Address not available', // Assuming address might be in root or we default
          time: record.updatedAt || record.createdAt
        }
      }
      // Fallback or Handle old format if necessary (though we expect new format now)
      if (record.locations && record.locations.length > 0) {
        const last = record.locations[record.locations.length - 1]
        return {
          lat: Number(last.latitude),
          lng: Number(last.longitude),
          address: last.address,
          time: last.time
        }
      }
      return null
    },
    getFullName(user) {
      if (!user.userId) return 'Unknown User'
      // Handle case where userId is just an ID string
      if (typeof user.userId === 'string') {
        return 'User ' + user.userId.substring(0, 6)
      }
      return `${user.userId.firstName || ''} ${user.userId.lastName || ''}`.trim() || 'Unknown User'
    },
    getLastLocation(user) {
      // Logic helper for template
      // If it's a "Group/Stop" object (from groupedHistory)
      if (user.type === 'STOP') {
        return {
          address: user.address,
          time: user.endTime // use end time for relative display
        }
      }
      return user.normalizedLocation || {}
    },
    getLastUpdateTime(user) {
      if (user.type === 'STOP') {
        // For stops, we might return duration or range, but this method is often for "moments"
        // Let's return the relative time of the stop end
        return this.$dayjs(user.endTime).fromNow()
      }
      const loc = this.getLastLocation(user)
      if (loc.time) {
        return this.$dayjs(loc.time).fromNow()
      }
      return '-'
    },
    formatDuration(ms) {
      if (!ms) return '0s'
      const seconds = Math.floor(ms / 1000)
      if (seconds < 60) return `${seconds}s`
      const minutes = Math.floor(seconds / 60)
      const remSeconds = seconds % 60
      if (minutes < 60) return `${minutes}m ${remSeconds}s`
      const hours = Math.floor(minutes / 60)
      const remMinutes = minutes % 60
      return `${hours}h ${remMinutes}m`
    },
    selectUser(user) {
      let key
      if (this.focusedUserId) {
        // In history mode, key is the record ID
        key = user._id
      } else {
        // In live mode, key is the user ID
        if (!user.userId) return
        key = typeof user.userId === 'string' ? user.userId : user.userId._id
        this.selectedUserId = key
      }

      if (this.showHistoryDetails) {
        this.selectedGroup = user
      }

      const markerObj = this.markersMap.get(key)

      if (markerObj && markerObj.marker && this.map) {
        // Pan to marker
        // Standard Marker uses getPosition(), AdvancedMarkerElement uses position
        const pos =
          markerObj.marker.position ||
          (markerObj.marker.getPosition ? markerObj.marker.getPosition() : null)
        if (pos) {
          this.map.panTo(pos)

          // Open Info Window
          if (markerObj.infowindow) {
            const openOptions = { map: this.map }
            if (
              this.AdvancedMarkerElement &&
              markerObj.marker instanceof this.AdvancedMarkerElement
            ) {
              openOptions.anchor = markerObj.marker
            } else {
              openOptions.anchor = markerObj.marker
            }
            // Unified open call usually works if passing anchor, but let's be safe
            markerObj.infowindow.open(openOptions)
          }
        }
      }

      // Fetch task details if needed
      if (this.showHistoryDetails && this.selectedGroup && this.selectedGroup.points) {
        this.selectedGroup.points.forEach((p) => {
          if (p.activity) {
            const tid = p.activity.caseTaskDetails || p.activity.taskDetails
            if (tid && !this.taskDetailsMap[tid]) {
              this.fetchTaskDetails(tid)
            }
          }
        })
      }
    },
    async fetchTaskDetails(taskId) {
      if (!taskId || this.taskDetailsMap[taskId]) return
      try {
        const response = await this.$axios.get(`/collection/case/task/${taskId}`)
        if (response.data && response.data.success) {
          this.$set(this.taskDetailsMap, taskId, response.data.task)
        }
      } catch (error) {
        console.error('Error fetching task details:', error)
      }
    },
    loadGoogleMapsScript() {
      const scriptTag = document.querySelector('script[src*="maps.googleapis.com"]')
      // If script exists, just init immediately (or wait for google obj if async loading still happening)
      if (scriptTag) {
        this.waitForGoogleMaps()
        return
      }

      const script = document.createElement('script')
      // Added loading=async and marker library
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_MAP_KEY}&loading=async&libraries=places,marker&callback=Function.prototype`
      script.defer = true
      script.async = true
      script.onload = this.waitForGoogleMaps
      document.head.appendChild(script)
    },
    waitForGoogleMaps() {
      // Check if google.maps is defined AND (importLibrary exists OR Map class exists)
      const isApiLoaded =
        typeof google !== 'undefined' &&
        typeof google.maps !== 'undefined' &&
        (typeof google.maps.importLibrary === 'function' || typeof google.maps.Map === 'function')

      if (isApiLoaded) {
        this.initMap()
      } else {
        setTimeout(this.waitForGoogleMaps, 300)
      }
    },
    // Unified helper to set marker position
    setMarkerPosition(marker, position) {
      if (this.AdvancedMarkerElement && marker instanceof this.AdvancedMarkerElement) {
        marker.position = position
      } else {
        marker.setPosition(position)
      }
    },

    // Unified helper to create marker
    createMarker(
      position,
      title,
      icon = null,
      glyph = null,
      background = null,
      borderColor = null,
      scale = 1
    ) {
      if (this.AdvancedMarkerElement && this.PinElement) {
        const pinOptions = {
          glyph: glyph,
          background: background,
          borderColor: borderColor,
          glyphColor: 'white',
          scale: scale
        }
        // If it's a "dot" (scale < 1 and empty glyph), handle cleanup if needed
        const pin = new this.PinElement(pinOptions)
        return new this.AdvancedMarkerElement({
          map: this.map,
          position: position,
          title: title,
          content: pin.element
        })
      }
      const options = {
        position: position,
        map: this.map,
        title: title,
        animation: google.maps.Animation.DROP
      }
      if (icon) {
        options.icon = icon
      }
      if (glyph) {
        options.label = {
          text: glyph.toString(),
          color: 'white',
          fontWeight: 'bold'
        }
      }
      return new google.maps.Marker(options)
    },

    // Unified cleanup
    removeMarker(marker) {
      if (marker.map) marker.map = null
      if (marker.setMap) marker.setMap(null)
    },

    async initMap() {
      // Safety check: if google API is not loaded yet, do nothing (wait for loader)
      if (typeof google === 'undefined' || typeof google.maps === 'undefined') return

      // Check if map container exists or if already initializing/initialized
      if (!this.$refs.map || this.isInitializing || this.map) return

      this.isInitializing = true
      try {
        let MapClass = null

        // Try to use importLibrary if available (Modern)
        if (google.maps.importLibrary) {
          try {
            const { Map } = await google.maps.importLibrary('maps')
            const { AdvancedMarkerElement, PinElement } = await google.maps.importLibrary('marker')
            MapClass = Map
            this.AdvancedMarkerElement = AdvancedMarkerElement
            this.PinElement = PinElement
            this.PinElement = PinElement
          } catch (libError) {
            console.warn('Modern load failed, falling back to legacy', libError)
          }
        }

        // Fallback for legacy load (if importLibrary missing OR failed but legacy available)
        if (!MapClass && typeof google.maps.Map === 'function') {
          MapClass = google.maps.Map
          if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
            this.AdvancedMarkerElement = google.maps.marker.AdvancedMarkerElement
            if (google.maps.marker.PinElement) {
              this.PinElement = google.maps.marker.PinElement
            }
          }
        }

        if (!MapClass) {
          if (this.initMapRetries < 3) {
            this.initMapRetries++
            console.warn(`Map class not found, retrying... (${this.initMapRetries}/3)`)
            this.isInitializing = false
            setTimeout(this.initMap, 1000)
            return
          }
          console.error('Failed to resolve Map class')
          return
        }

        // Reset retries on success
        this.initMapRetries = 0

        this.map = new MapClass(this.$refs.map, {
          center: this.center,
          zoom: this.zoom,
          // mapId is required for AdvancedMarkerElement. Fallback to DEMO_MAP_ID
          mapId: process.env.VUE_APP_MAP_ID || 'DEMO_MAP_ID',
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false
        })

        // Initialize Directions Service and Renderer
        this.directionsService = new google.maps.DirectionsService()
        this.directionsRenderer = new google.maps.DirectionsRenderer({
          map: this.map,
          suppressMarkers: true // We will draw our own markers
        })

        this.updateMarkers()
      } catch (error) {
        console.error('Error initializing Google Maps:', error)
      } finally {
        this.isInitializing = false
      }
    },
    updateMarkers() {
      if (!this.map) {
        if (this.$refs.map && !this.map && !this.isInitializing) {
          this.initMap()
        }
        return
      }

      // Clear directions if switching modes or refreshing
      if (this.directionsRenderer) {
        this.directionsRenderer.setDirections({ routes: [] })
      }
      if (this.fallbackPolyline) {
        this.fallbackPolyline.setMap(null)
        this.fallbackPolyline = null
      }

      const bounds = new google.maps.LatLngBounds()
      const currentKeys = new Set()
      let hasValidLocations = false
      const allHistoryPoints = [] // For route drawing (ALL points)

      const itemsToDraw = this.focusedUserId ? this.groupedHistory : this.processedUsers

      // We use loops to accumulate bounds and finding items
      // In history mode, we loop groupedHistory for markers
      // In live mode, we loop processedUsers (which here are keys of map but actually we have array)

      // Correction: The outer logic structure of this method was loop over `processedUsers`
      // We need to change that. We need to iterate the appropriate list for MARKERS
      // BUT we need to iterate `processedUsers` for BOUNDS and ROUTE (if history)

      // Let's split the logic.

      // 1. Calculate Bounds & Route Points (Using ALL raw points in History mode, or just current points in Live)
      this.processedUsers.forEach((u) => {
        const l = u.normalizedLocation
        if (l && l.lat && l.lng) {
          const pos = { lat: Number(l.lat), lng: Number(l.lng) }
          bounds.extend(pos)
          if (this.focusedUserId) {
            allHistoryPoints.push(pos)
          }
          hasValidLocations = true
        }
      })

      // 2. Determine Items for Markers
      const loopItems = this.focusedUserId ? this.groupedHistory : this.processedUsers

      loopItems.forEach((item, index) => {
        // Determine Location
        let loc
        if (this.focusedUserId) {
          // item is a Stop Group
          loc = { lat: item.lat, lng: item.lng, address: item.address }
        } else {
          // item is User record
          loc = item.normalizedLocation
        }

        if (!loc || loc.lat === undefined || loc.lng === undefined) return

        const position = { lat: Number(loc.lat), lng: Number(loc.lng) }

        // Unique Key
        let uniqueKey
        let markerTitle

        if (this.focusedUserId) {
          uniqueKey = item._id
          markerTitle = `Stop ${item.index}`
          if (index > 0 && index < loopItems.length - 1) {
            markerTitle += ' (Intermediate)'
          }
        } else {
          uniqueKey = item.userId._id || (typeof item.userId === 'string' ? item.userId : 'unknown')
          markerTitle = this.getFullName(item)
        }

        currentKeys.add(uniqueKey)

        const userName = this.focusedUserId ? `Stop ${item.index}` : this.getFullName(item)
        const userAddress = loc.address || 'No address available'

        let timeInfo = ''
        if (this.focusedUserId) {
          timeInfo = `
             <div style="margin-top:4px; font-size:12px; color:#555;">
               <div><strong>Arrived:</strong> ${this.$dayjs(item.startTime).format(
                 'hh:mm:ss A'
               )}</div>
               <div><strong>Departed:</strong> ${this.$dayjs(item.endTime).format(
                 'hh:mm:ss A'
               )}</div>
               <div style="font-weight: 600; color:#1976D2; margin-top:2px;"><strong>Duration:</strong> ${
                 item.durationText
               }</div>
             </div>
           `
        } else {
          const time = loc.time ? this.$dayjs(loc.time).format('DD MMM YYYY, hh:mm A') : ''
          timeInfo = `<div style="font-size: 10px; color: #999; text-align: right;">${time}</div>`
        }

        const relativeTimeStr = loc.time
          ? this.$dayjs(loc.time).fromNow()
          : item.endTime
          ? this.$dayjs(item.endTime).fromNow()
          : ''
        // Use aggregated battery for stop, or single for user
        const batt = item.batteryLevel
        const batteryLevel = batt !== undefined && batt !== null ? `${batt.toFixed(0)}%` : 'N/A'

        const contentString = `
                <div style="min-width: 220px; padding: 4px;">
                  <div style="border-bottom: 1px solid #eee; padding-bottom: 8px; margin-bottom: 8px;">
                      <h4 style="font-weight: 600; font-size: 16px; margin: 0; color: #1a1a1a;">${userName}</h4>
                      <span style="font-size: 11px; color: #666;">${
                        this.focusedUserId ? 'Visited' : 'Last active'
                      }: ${relativeTimeStr}</span>
                  </div>
                  <div style="margin-bottom: 8px;">
                      <p style="font-size: 12px; color: #444; margin: 0 0 4px 0; display: flex; align-items: flex-start;">
                          <span style="margin-right: 4px;">📍</span> ${userAddress}
                      </p>
                      <p style="font-size: 12px; color: #444; margin: 4px 0 0 0; display: flex; align-items: center; border-bottom: 1px solid #eee; padding-bottom: 8px; margin-bottom: 8px;  ">
                          <span style="margin-right: 4px;">🔋</span>${batteryLevel}
                      </p>
                      ${timeInfo}
                  </div>
                </div>
              `

        let markerObj = this.markersMap.get(uniqueKey)

        if (!markerObj) {
          // Create New
          let glyph = ''
          let background = null
          let borderColor = null
          let zIndex = null
          let scale = 1

          if (this.focusedUserId) {
            // History Mode (Stops)
            zIndex = 100 + index // ensure sequence layering

            if (index === 0) {
              // Start: Green
              glyph = String(item.index)
              background = '#4CAF50'
              borderColor = '#388E3C'
              zIndex = 200 // Top priority
            } else if (index === itemsToDraw.length - 1) {
              // End: Red
              glyph = String(item.index)
              background = '#F44336'
              borderColor = '#D32F2F'
              zIndex = 200 // Top priority
            } else {
              // Intermediate: Blue with Number
              glyph = String(item.index)
              background = '#1A73E8' // Google Blue
              borderColor = '#1557B0'
              scale = 0.8
            }
          } else {
            // Live Mode
            glyph = this.getFullName(item).charAt(0).toUpperCase()
            background = '#FB8C00' // Orange
            borderColor = '#EF6C00'
          }

          const marker = this.createMarker(
            position,
            markerTitle,
            null,
            glyph,
            background,
            borderColor,
            scale
          )

          if (zIndex) {
            // Basic Marker setZIndex, AdvancedMarkerElement using content style z-index if needed
            if (marker.setZIndex) marker.setZIndex(zIndex)
            else if (marker.element) marker.element.style.zIndex = zIndex
          }

          const infowindow = new google.maps.InfoWindow({
            content: contentString,
            disableAutoPan: true
          })

          marker.addListener('click', () => {
            // If grouped stop, we need to pass a compatible object or handle it
            if (this.focusedUserId) {
              this.selectUser(item)
            } else {
              this.selectUser(item)
            }
          })

          markerObj = { marker, infowindow }
          this.markersMap.set(uniqueKey, markerObj)
        } else {
          // Update Existing
          this.setMarkerPosition(markerObj.marker, position)
          markerObj.infowindow.setContent(contentString)
        }
        // CONTROL INFO WINDOW VISIBILITY
        if (!this.focusedUserId) {
          // Live Mode: Open all (or usually just on click, but kept user logic)
          markerObj.infowindow.open({
            anchor: markerObj.marker,
            map: this.map
          })
        } else {
          // History Mode: Open ONLY first and last STILL points
          // index matches our loop over processingUsers.
          if (index === 0 || index === itemsToDraw.length - 1) {
            markerObj.infowindow.open({
              anchor: markerObj.marker,
              map: this.map
            })
          } else {
            markerObj.infowindow.close()
          }
        }
      })

      // Remove markers that are no longer present
      for (const [key, markerObj] of this.markersMap) {
        if (!currentKeys.has(key)) {
          this.removeMarker(markerObj.marker)
          this.markersMap.delete(key)
        }
      }

      // Fit bounds
      if (hasValidLocations) {
        // If detailed history, always re-fit to show the whole path
        if (this.focusedUserId || !this.initialFitDone) {
          this.map.fitBounds(bounds)
          if (this.markersMap.size === 1) {
            this.map.setZoom(15)
          }
          this.initialFitDone = true
        }

        if (this.focusedUserId && allHistoryPoints.length > 1) {
          this.drawRoute(allHistoryPoints)
        }
      }
    },
    drawRoute(points) {
      if (!this.directionsService || !this.directionsRenderer || points.length < 2) return

      const origin = points[0]
      const destination = points[points.length - 1]

      // Sampling waypoints (max 25 total points supported by Directions API usually,
      // 23 waypoints + origin + dest)
      const maxWaypoints = 23
      let waypoints = []

      if (points.length > 2) {
        const intermediate = points.slice(1, -1)
        if (intermediate.length <= maxWaypoints) {
          waypoints = intermediate.map((p) => ({ location: p, stopover: false }))
        } else {
          // Downsample
          const step = intermediate.length / maxWaypoints
          for (let i = 0; i < maxWaypoints; i++) {
            const idx = Math.floor(i * step)
            waypoints.push({ location: intermediate[idx], stopover: false })
          }
        }
      }

      this.directionsService.route(
        {
          origin: origin,
          destination: destination,
          waypoints: waypoints,
          travelMode: 'DRIVING'
        },
        (response, status) => {
          if (status === 'OK') {
            if (this.directionsRenderer) {
              this.directionsRenderer.setDirections(response)
            }
          } else {
            console.warn('Directions request failed (' + status + '), falling back to polyline.')
            this.drawFallbackPolyline(points)
          }
        }
      )
    },
    drawFallbackPolyline(points) {
      if (this.fallbackPolyline) {
        this.fallbackPolyline.setMap(null)
      }
      this.fallbackPolyline = new google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: '#1A73E8', // Google Blue
        strokeOpacity: 0.8,
        strokeWeight: 5,
        icons: [
          {
            icon: {
              path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              strokeColor: '#ffffff', // White arrow
              fillColor: '#1A73E8', // Blue fill
              fillOpacity: 1,
              scale: 2
            },
            offset: '100%',
            repeat: '100px'
          }
        ]
      })
      this.fallbackPolyline.setMap(this.map)
    },
    getBatteryIcon(level) {
      if (level >= 90) return 'mdi-battery'
      if (level >= 80) return 'mdi-battery-80'
      if (level >= 60) return 'mdi-battery-60'
      if (level >= 40) return 'mdi-battery-40'
      if (level >= 20) return 'mdi-battery-20'
      return 'mdi-battery-outline'
    },
    getBatteryColor(level) {
      if (level > 20) return 'green'
      return 'red'
    },
    getCounts(items) {
      if (!items) return {}
      return items.reduce((acc, curr) => {
        acc[curr] = (acc[curr] || 0) + 1
        return acc
      }, {})
    },
    calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371 // Radius of the earth in km
      const dLat = this.deg2rad(lat2 - lat1)
      const dLon = this.deg2rad(lon2 - lon1)
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.deg2rad(lat1)) *
          Math.cos(this.deg2rad(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      const d = R * c // Distance in km
      return d
    },
    deg2rad(deg) {
      return deg * (Math.PI / 180)
    }
  }
}
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #ddd;
  border-radius: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #ccc;
}
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
